import { usePrivy } from "@privy-io/react-auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError, createApiClient, type ApiClient } from "../api/client";
import type { AdminMeResponse } from "../api/types";
import { config } from "../config";

/**
 * What the console knows about the person at the keyboard.
 *
 * - `loading`      Privy is booting, or /admin/me is in flight.
 * - `anonymous`    Nobody is signed in (or the token expired: same screen).
 * - `refused`      Signed in to Privy, but the API says this DID is not staff.
 *                  This is the onboarding path, not an error path.
 * - `ready`        /admin/me returned 200; `staff` is populated.
 * - `unreachable`  The API could not be reached, or answered 5xx.
 */
export type SessionStatus =
  | "loading"
  | "anonymous"
  | "refused"
  | "ready"
  | "unreachable";

export interface SessionValue {
  status: SessionStatus;
  /** Populated only when `status === "ready"`. */
  staff: AdminMeResponse | null;
  /** The signed-in Privy DID, available even while access is refused. */
  privyDid: string | null;
  /** Message to show on `unreachable`. */
  error: string | null;
  api: ApiClient;
  login: () => void;
  logout: () => void;
  refresh: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { ready, authenticated, user, getAccessToken, login, logout } = usePrivy();
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [staff, setStaff] = useState<AdminMeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Privy's `getAccessToken` identity is not stable across renders, so the
  // client is built once against a ref. Rebuilding it every render would
  // re-trigger every effect that depends on `api`.
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiBaseUrl,
        getToken: () => getAccessTokenRef.current(),
      }),
    [],
  );

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    if (!ready) {
      setStatus("loading");
      return;
    }
    if (!authenticated) {
      setStaff(null);
      setStatus("anonymous");
      return;
    }

    let cancelled = false;
    setStatus("loading");
    setError(null);

    api
      .me()
      .then((me) => {
        if (cancelled) return;
        setStaff(me);
        setStatus("ready");
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setStaff(null);
        if (err instanceof ApiError && err.status === 403) {
          // Deliberate: a 403 is a person who has signed in and now needs an
          // admin to grant them a role. The UI treats it as onboarding.
          // Fire-and-forget bootstrap: the role guard never creates a user
          // row, so without this a brand-new operator would be invisible to
          // grant-by-email. /v1/auth/me creates the row and captures their
          // email; its outcome changes nothing on this screen.
          void api.bootstrap().catch(() => {});
          setStatus("refused");
          return;
        }
        if (err instanceof ApiError && err.status === 401) {
          // The Privy session is stale as far as the API is concerned. Drop it
          // so the sign-in button starts a clean login instead of looping.
          void logoutRef.current();
          setStatus("anonymous");
          return;
        }
        setError(err instanceof Error ? err.message : "Something went wrong");
        setStatus("unreachable");
      });

    return () => {
      cancelled = true;
    };
  }, [ready, authenticated, api, reloadKey]);

  const refresh = useCallback(() => setReloadKey((n) => n + 1), []);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      staff,
      privyDid: user?.id ?? null,
      error,
      api,
      login: () => login(),
      logout: () => void logout(),
      refresh,
    }),
    [status, staff, user?.id, error, api, login, logout, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession must be used inside <SessionProvider>");
  return value;
}

/** The signed-in staff record. Only call this from behind the `ready` gate. */
export function useStaff(): AdminMeResponse {
  const { staff } = useSession();
  if (!staff) throw new Error("useStaff called outside an authenticated route");
  return staff;
}
